import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styles from './AdminPanel.module.css';
import {
  LayoutDashboard, Plus, Users, Inbox, ScrollText, Folder, X,
  KeyRound, Lock, User, FileText, ArrowUpRight, Mail, RefreshCw, Upload,
  Download, Trash2, Package, Clock, Globe, CheckCircle, AlertCircle,
  MailOpen, Eye, EyeOff, Calendar, HardDrive, Copy, ExternalLink, Pencil, Power,
  Settings, ChevronLeft, ChevronRight, LogOut
} from 'lucide-react';
import { FileIcon } from '../fileIcons';

/* ─── Helpers ─────────────────────────────────────────────── */

function api(method, url, data, config = {}) {
  const key = localStorage.getItem('adminKey') || '';
  const separator = url.includes('?') ? '&' : '?';
  return axios({ method, url: '/api' + url + separator + 'adminKey=' + key, data, ...config });
}

function formatSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const btnIcon = { display: 'inline-flex', alignItems: 'center', gap: '5px' };

const AUDIT_LABELS = {
  login_ok: 'Zalogowano', login_failed: 'Błąd logowania',
  password_changed: 'Zmiana hasła', password_reset_admin: 'Reset hasła (admin)',
  user_created: 'Utworzono użytkownika', user_deleted: 'Usunięto użytkownika',
  user_enabled: 'Włączono konto', user_disabled: 'Wyłączono konto',
  inbox_access_granted: 'Nadano dostęp do Odebrane', inbox_access_revoked: 'Odebrano dostęp do Odebrane',
  share_created: 'Utworzono udostępnienie', share_deleted: 'Usunięto udostępnienie',
  inbox_received: 'Otrzymano przesyłkę', inbox_deleted: 'Usunięto przesyłkę'
};

const AUDIT_COLORS = {
  login_ok: '#86efac', login_failed: '#f87171',
  password_changed: '#fcd34d', password_reset_admin: '#fcd34d',
  user_created: '#86efac', user_deleted: '#f87171',
  user_enabled: '#86efac', user_disabled: '#f87171',
  inbox_access_granted: '#86efac', inbox_access_revoked: '#f87171',
  share_created: '#7dd3fc', share_deleted: '#f87171',
  inbox_received: '#7dd3fc', inbox_deleted: '#f87171'
};

/* ─── Kompaktowa karta udostępnienia ──────────────────────── */

function ShareCard({ s, selected, onToggleSelect, folders, users, onMoveToFolder, onCopy, onEdit, onShowLogs, onToggleActive, onDelete }) {
  const [folderOpen, setFolderOpen] = useState(false);

  const expired = s.expiresAt && Date.now() > s.expiresAt;
  const limitHit = s.downloadLimit > 0 && s.downloads >= s.downloadLimit;
  const status = !s.active ? 'amber' : expired || limitHit ? 'red' : 'green';
  const statusTxt = !s.active ? 'Wyłączony' : expired ? 'Wygasł' : limitHit ? 'Limit' : 'Aktywny';

  const shareUrl = `${window.location.origin}/s/${s.id}`;
  const folderName = folders.find(f => f.id === s.folderId)?.name || null;
  const userName = users.find(u => u.id === s.userId)?.name || s.userName || '';

  const daysLeft = s.expiresAt ? Math.ceil((s.expiresAt - Date.now()) / 86400000) : null;
  const dateColor = daysLeft !== null && daysLeft <= 3 ? '#f87171' : daysLeft !== null && daysLeft <= 7 ? '#fbbf24' : '#7dd3fc';

  return (
    <div className={styles.shareCompact} style={{ position: 'relative', zIndex: folderOpen ? 10 : 1 }}>
      <div className={styles.shareCompactTop}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(s.id)}
          style={{ width: '14px', height: '14px', flexShrink: 0, cursor: 'pointer', accentColor: '#7c3aed' }}
        />
        <span className={styles.shareCompactName} title={s.name}>{s.name}</span>
        <span className={styles.shareCompactSpacer} />
        {s.hasPassword && <span className="badge blue"><Lock size={11} strokeWidth={2} /> Hasło</span>}
        {s.expiresAt && (
          <span className={styles.shareChipDate} style={{ color: dateColor }}>
            <Calendar size={12} strokeWidth={1.75} /> {formatDate(s.expiresAt)}
          </span>
        )}
        <span className={styles.shareChipFiles}>
          <FileText size={12} strokeWidth={1.75} /> {s.fileCount} {s.fileCount === 1 ? 'plik' : 'pliki'}
        </span>
        <span className={styles.shareChipDl}>
          <Download size={12} strokeWidth={1.75} /> {s.downloadLimit > 0 ? `${s.downloads}/${s.downloadLimit}` : s.downloads}
        </span>
        {userName && (
          <span className={styles.shareChipUser}>
            <User size={11} strokeWidth={1.75} /> {userName}
          </span>
        )}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <span className={styles.shareChipFolder} onClick={() => setFolderOpen(p => !p)}>
            <Folder size={11} strokeWidth={1.75} /> {folderName || 'Bez folderu'} ▾
          </span>
          {folderOpen && (
            <div style={{ position: 'absolute', left: 0, top: '110%', background: '#1a1a22', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', minWidth: '160px', maxHeight: '220px', overflowY: 'auto', zIndex: 999, padding: '4px', boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
              <div
                onClick={() => { onMoveToFolder(s.id, null); setFolderOpen(false); }}
                style={{ padding: '6px 10px', fontSize: '12px', cursor: 'pointer', borderRadius: '5px', color: !s.folderId ? '#c4b5fd' : '#fff', background: !s.folderId ? 'rgba(124,58,237,0.16)' : 'transparent' }}
              >
                Bez folderu
              </div>
              {folders.map(f => (
                <div
                  key={f.id}
                  onClick={() => { onMoveToFolder(s.id, f.id); setFolderOpen(false); }}
                  style={{ padding: '6px 10px', fontSize: '12px', cursor: 'pointer', borderRadius: '5px', display: 'flex', alignItems: 'center', gap: '6px', color: s.folderId === f.id ? '#c4b5fd' : '#fff', background: s.folderId === f.id ? 'rgba(124,58,237,0.16)' : 'transparent' }}
                >
                  <Folder size={12} strokeWidth={1.75} /> {f.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <span className={`badge ${status}`}>{statusTxt}</span>
      </div>

      <div className={styles.shareCompactBottom}>
        <code className={styles.shareCompactUrl}>{shareUrl}</code>
        <button onClick={() => onCopy(shareUrl)} title="Kopiuj link">
          <Copy size={15} strokeWidth={1.75} /><span className={styles.btnLabel}>Kopiuj</span>
        </button>
        <a href={shareUrl} target="_blank" rel="noreferrer" style={{ display: 'contents' }}>
          <button title="Otwórz link">
            <ExternalLink size={15} strokeWidth={1.75} /><span className={styles.btnLabel}>Otwórz</span>
          </button>
        </a>
        <button onClick={() => onEdit(s)} title="Edytuj">
          <Pencil size={15} strokeWidth={1.75} /><span className={styles.btnLabel}>Edytuj</span>
        </button>
        <button onClick={() => onShowLogs(s)} title="Logi pobrań">
          <FileText size={15} strokeWidth={1.75} /><span className={styles.btnLabel}>Logi</span>
        </button>
        <button onClick={() => onToggleActive(s.id)} title={s.active ? 'Wyłącz link' : 'Włącz link'}>
          <Power size={15} strokeWidth={1.75} /><span className={styles.btnLabel}>{s.active ? 'Wyłącz' : 'Włącz'}</span>
        </button>
        <button className="danger" onClick={() => onDelete(s.id)} title="Usuń">
          <Trash2 size={15} strokeWidth={1.75} /><span className={styles.btnLabel}>Usuń</span>
        </button>
      </div>
    </div>
  );
}

/* ─── Panel administratora ────────────────────────────────── */

export default function AdminPanel() {
  // auth
  const [adminKey, setAdminKey] = useState(localStorage.getItem('adminKey') || '');
  const [authed, setAuthed] = useState(false);

  // nawigacja
  const [tab, setTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === '1'
  );

  // dane
  const [shares, setShares] = useState([]);
  const [users, setUsers] = useState([]);
  const [folders, setFolders] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');

  // filtrowanie dashboardu
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedShares, setSelectedShares] = useState(new Set());

  // formularz nowego udostępnienia
  const [files, setFiles] = useState([]);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [expiryDays, setExpiryDays] = useState('7');
  const [downloadLimit, setDownloadLimit] = useState('0');
  const [message, setMessage] = useState('');
  const [newShareFolderId, setNewShareFolderId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [createdLink, setCreatedLink] = useState(null);
  const [dragging, setDragging] = useState(false);

  // modal edycji
  const [editingShare, setEditingShare] = useState(null);
  const [editName, setEditName] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editExpiryDays, setEditExpiryDays] = useState('7');
  const [editDownloadLimit, setEditDownloadLimit] = useState('0');
  const [editRemovePass, setEditRemovePass] = useState(false);

  // użytkownicy
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPass, setNewUserPass] = useState('');
  const [userError, setUserError] = useState('');
  const [resetPassUser, setResetPassUser] = useState(null);
  const [resetPassValue, setResetPassValue] = useState('');
  const [resetPassError, setResetPassError] = useState('');

  // pozostałe modale / filtry
  const [openUpload, setOpenUpload] = useState(null);
  const [logShare, setLogShare] = useState(null);
  const [auditFilter, setAuditFilter] = useState('all');
  const [auditSearchQuery, setAuditSearchQuery] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [foldersModalOpen, setFoldersModalOpen] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      localStorage.setItem('sidebarCollapsed', prev ? '0' : '1');
      return !prev;
    });
  };

  const logout = () => {
    localStorage.removeItem('adminKey');
    setAuthed(false);
  };

  /* ─── Pobieranie danych ─── */

  const fetchShares = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('get', '/admin/shares');
      setShares(res.data);
      setAuthed(true);
    } catch (e) {
      if (e.response?.status === 401) setAuthed(false);
    } finally { setLoading(false); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api('get', '/admin/users');
      setUsers(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await api('get', '/folders');
      setFolders(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchInbox = useCallback(async () => {
    try {
      const res = await api('get', '/inbox');
      setInbox(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    try {
      const res = await api('get', '/admin/audit-log');
      setAuditLog(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); setAuditLog([]); }
  }, []);

  useEffect(() => {
    if (adminKey) { fetchShares(); fetchUsers(); fetchFolders(); fetchInbox(); fetchAuditLog(); }
  }, []);

  const login = async () => {
    localStorage.setItem('adminKey', adminKey);
    await fetchShares();
    await fetchUsers();
    await fetchFolders();
    await fetchInbox();
    await fetchAuditLog();
  };

  /* ─── Foldery ─── */

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api('post', '/folders', { name: newFolderName });
      setNewFolderName('');
      fetchFolders();
      showToast('Folder utworzony!');
    } catch { showToast('Błąd'); }
  };

  const deleteFolder = async (id) => {
    if (!window.confirm('Usunąć folder? Linki w tym folderze zostaną przeniesione do "Bez folderu".')) return;
    try {
      await api('delete', `/folders/${id}`);
      if (selectedFolder === id) setSelectedFolder('all');
      fetchFolders();
      fetchShares();
      showToast('Folder usunięty');
    } catch { showToast('Błąd'); }
  };

  const moveShareToFolder = async (shareId, folderId) => {
    try {
      await api('patch', `/shares/${shareId}/folder`, { folderId });
      fetchShares();
      showToast('Przeniesiono!');
    } catch { showToast('Błąd'); }
  };

  /* ─── Udostępnienia ─── */

  const handleFiles = (newFiles) => {
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size));
      const added = Array.from(newFiles).filter(f => !existing.has(f.name + f.size));
      if (prev.length === 0 && added.length > 0 && !name.trim()) {
        setName(added[0].name.replace(/\.[^.]+$/, ''));
      }
      return [...prev, ...added];
    });
  };

  const createShare = async () => {
    if (!files.length) { showToast('Dodaj przynajmniej jeden plik!'); return; }
    setUploading(true); setUploadProgress(0); setCreatedLink(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      formData.append('name', name || 'Udostępnione pliki');
      if (password) formData.append('password', password);
      formData.append('expiryDays', expiryDays);
      formData.append('downloadLimit', downloadLimit);
      if (message) formData.append('message', message);
      if (newShareFolderId) formData.append('folderId', newShareFolderId);
      const key = localStorage.getItem('adminKey') || '';
      const res = await axios.post('/api/admin/shares?adminKey=' + key, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / e.total) * 100)),
      });
      setCreatedLink({ url: `${window.location.origin}/s/${res.data.id}` });
      setFiles([]); setName(''); setPassword(''); setMessage(''); setNewShareFolderId('');
      fetchShares();
      showToast('Link utworzony!');
    } catch (e) { showToast(e.response?.data?.error || 'Błąd podczas przesyłania'); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const toggleSelectShare = (id) => {
    setSelectedShares(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const bulkDeleteShares = async () => {
    const ids = [...selectedShares];
    if (ids.length === 0) return;
    if (!window.confirm(`Usunąć ${ids.length} ${ids.length === 1 ? 'link' : 'linków'} wraz z plikami? Tej operacji nie można cofnąć.`)) return;
    try {
      await api('post', '/admin/shares/bulk-delete', { ids });
      setShares(prev => prev.filter(s => !selectedShares.has(s.id)));
      setSelectedShares(new Set());
      showToast(`Usunięto ${ids.length} ${ids.length === 1 ? 'link' : 'linków'}`);
    } catch { showToast('Błąd'); }
  };

  const deleteExpiredShares = async () => {
    if (expiredCount === 0) return;
    if (!window.confirm(`Usunąć ${expiredCount} wygasłych ${expiredCount === 1 ? 'link' : 'linków'} wraz z plikami? Tej operacji nie można cofnąć.`)) return;
    try {
      const res = await api('post', '/admin/shares/delete-expired');
      setShares(prev => prev.filter(s => !(s.expiresAt && Date.now() > s.expiresAt)));
      showToast(`Usunięto ${res.data.deleted} wygasłych ${res.data.deleted === 1 ? 'link' : 'linków'}`);
    } catch { showToast('Błąd'); }
  };

  const deleteShare = async (id) => {
    if (!window.confirm('Na pewno usunąć ten link i wszystkie jego pliki?')) return;
    try {
      await api('delete', `/admin/shares/${id}`);
      setShares(prev => prev.filter(s => s.id !== id));
      showToast('Usunięto');
    } catch { showToast('Błąd'); }
  };

  const toggleShare = async (id) => {
    try {
      const res = await api('patch', `/admin/shares/${id}`, { toggleActive: true });
      setShares(prev => prev.map(s => s.id === id ? { ...s, active: res.data.share.active } : s));
    } catch { showToast('Błąd'); }
  };

  const openEdit = (s) => {
    setEditingShare(s);
    setEditName(s.name || '');
    setEditMessage(s.message || '');
    setEditPassword('');
    setEditRemovePass(false);
    const daysLeft = s.expiresAt ? Math.max(0, Math.round((s.expiresAt - Date.now()) / 86400000)) : 0;
    setEditExpiryDays(s.expiresAt ? String(daysLeft || 7) : '0');
    setEditDownloadLimit(String(s.downloadLimit || 0));
  };

  const saveEdit = async () => {
    try {
      const payload = {
        name: editName,
        message: editMessage,
        expiryDays: editExpiryDays,
        downloadLimit: editDownloadLimit,
      };
      if (editRemovePass) payload.password = '';
      else if (editPassword) payload.password = editPassword;
      await api('patch', `/admin/shares/${editingShare.id}`, payload);
      setEditingShare(null);
      fetchShares();
      showToast('Zmiany zapisane!');
    } catch { showToast('Błąd zapisu'); }
  };

  /* ─── Użytkownicy ─── */

  const createUser = async () => {
    setUserError('');
    if (!newUserName || !newUserEmail || !newUserPass) { setUserError('Wypełnij wszystkie pola'); return; }
    try {
      await api('post', '/admin/users', { name: newUserName, email: newUserEmail, password: newUserPass });
      setNewUserName(''); setNewUserEmail(''); setNewUserPass('');
      fetchUsers();
      showToast('Użytkownik utworzony!');
    } catch (e) { setUserError(e.response?.data?.error || 'Błąd'); }
  };

  const toggleUser = async (id) => {
    try {
      const res = await api('patch', `/admin/users/${id}`);
      setUsers(prev => prev.map(u => u.id === id ? { ...u, active: res.data.active } : u));
    } catch { showToast('Błąd'); }
  };

  const toggleInbox = async (id) => {
    try {
      const res = await api('patch', `/admin/users/${id}`, { toggleInbox: true });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, inboxAccess: res.data.inboxAccess } : u));
      showToast(res.data.inboxAccess ? 'Nadano dostęp do Odebrane' : 'Odebrano dostęp do Odebrane');
    } catch { showToast('Błąd'); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Na pewno usunąć tego użytkownika?')) return;
    try {
      await api('delete', `/admin/users/${id}`);
      setUsers(prev => prev.filter(u => u.id !== id));
      showToast('Usunięto');
    } catch { showToast('Błąd'); }
  };

  const resetUserPassword = async () => {
    setResetPassError('');
    if (!resetPassValue || resetPassValue.length < 6) { setResetPassError('Hasło musi mieć minimum 6 znaków'); return; }
    try {
      await api('patch', `/admin/users/${resetPassUser.id}`, { newPassword: resetPassValue });
      setResetPassUser(null);
      setResetPassValue('');
      showToast('Hasło zmienione!');
    } catch (e) { setResetPassError(e.response?.data?.error || 'Błąd'); }
  };

  /* ─── Odebrane ─── */

  const openInboxItem = async (id) => {
    try {
      const res = await api('get', `/inbox/${id}`);
      setOpenUpload(res.data);
    } catch { showToast('Błąd'); }
  };

  const deleteInboxItem = async (id) => {
    if (!window.confirm('Usunąć tę przesyłkę i wszystkie jej pliki?')) return;
    try {
      await api('delete', `/inbox/${id}`);
      setInbox(prev => prev.filter(u => u.id !== id));
      setOpenUpload(null);
      showToast('Usunięto');
    } catch { showToast('Błąd'); }
  };

  const downloadInboxFile = (uploadId, filename) => {
    const key = localStorage.getItem('adminKey') || '';
    window.open(`/api/inbox/${uploadId}/files/${encodeURIComponent(filename)}?adminKey=${key}`, '_blank');
  };

  const downloadInboxZip = (uploadId) => {
    const key = localStorage.getItem('adminKey') || '';
    window.open(`/api/inbox/${uploadId}/zip?adminKey=${key}`, '_blank');
  };

  /* ─── Logi systemu ─── */

  const clearAuditLog = async () => {
    if (!window.confirm('Wyczyścić wszystkie logi systemu? Tej operacji nie można cofnąć.')) return;
    try {
      await api('delete', '/admin/audit-log');
      setAuditLog([]);
      showToast('Logi wyczyszczone');
    } catch { showToast('Błąd'); }
  };

  /* ─── Pomocnicze ─── */

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => showToast('Skopiowano!'))
      .catch(() => showToast('Skopiuj ręcznie'));
  };

  const expiredCount = shares.filter(s => s.expiresAt && Date.now() > s.expiresAt).length;
  const noFolderCount = shares.filter(s => !s.folderId).length;

  const matchesSearch = (s) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const expired = s.expiresAt && Date.now() > s.expiresAt;
    const limitHit = s.downloadLimit > 0 && s.downloads >= s.downloadLimit;
    const haystack = [
      s.name,
      s.message,
      s.id,
      s.createdAt ? new Date(s.createdAt).toLocaleDateString('pl-PL') : '',
      s.expiresAt ? new Date(s.expiresAt).toLocaleDateString('pl-PL') : '',
      folders.find(f => f.id === s.folderId)?.name || '',
      users.find(u => u.id === s.userId)?.name || s.userName || '',
      (s.files || []).map(f => f.name || f.originalName || '').join(' '),
      !s.active ? 'wyłączony nieaktywny' : expired ? 'wygasł wygasły' : limitHit ? 'limit' : 'aktywny',
      s.hasPassword ? 'hasło z hasłem zabezpieczony' : 'bez hasła',
      String(s.downloads),
      String(s.downloadLimit || ''),
      String(s.fileCount || ''),
      (s.downloadLog || []).map(d => d.ip || '').join(' ')
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  };

  const visibleShares = shares
    .filter(s => {
      if (selectedFolder === 'none' && s.folderId) return false;
      if (selectedFolder !== 'all' && selectedFolder !== 'none' && s.folderId !== selectedFolder) return false;
      return matchesSearch(s);
    })
    .sort((a, b) => {
      if (sortBy === 'oldest') return a.createdAt - b.createdAt;
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'downloads') return b.downloads - a.downloads;
      return b.createdAt - a.createdAt;
    });

  /* ─── Ekran logowania ─── */

  if (!authed) {
    return (
      <div className={styles.loginWrap}>
        <div className={styles.loginCard}>
          <div className={styles.loginIcon}><KeyRound size={42} strokeWidth={1.5} /></div>
          <h1>FileShare Admin</h1>
          <p>Wprowadź klucz dostępu administratora</p>
          <div className="field" style={{ marginTop: '1.5rem' }}>
            <label>Klucz admina</label>
            <input type="password" value={adminKey} onChange={e => setAdminKey(e.target.value)} onKeyDown={e => e.key === 'Enter' && login()} placeholder="Twój klucz z pliku .env" autoFocus />
          </div>
          <button className="primary" style={{ width: '100%' }} onClick={login}>Zaloguj się</button>
        </div>
      </div>
    );
  }

  /* ─── Panel ─── */

  return (
    <div className={styles.layout}>
      <aside className={`${styles.sidebar} ${sidebarCollapsed ? styles.sidebarCollapsed : ''}`}>
        <div className={styles.sidebarHeader}>
          <span className={styles.logo}>FileShare</span>
          <button
            className={styles.collapseBtn}
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Rozwiń panel' : 'Zwiń panel'}
          >
            {sidebarCollapsed ? <ChevronRight size={16} strokeWidth={2} /> : <ChevronLeft size={15} strokeWidth={2} />}
          </button>
        </div>

        <nav>
          <button className={`${styles.navBtn} ${tab === 'dashboard' ? styles.active : ''}`} onClick={() => setTab('dashboard')} title="Dashboard">
            <LayoutDashboard size={20} strokeWidth={1.75} />
            <span className={styles.navLabel}>Dashboard</span>
            <span className={styles.navLabelShort}>Linki</span>
          </button>
          <button className={`${styles.navBtn} ${tab === 'new' ? styles.active : ''}`} onClick={() => { setTab('new'); setCreatedLink(null); }} title="Nowe udostępnienie">
            <Plus size={20} strokeWidth={1.75} />
            <span className={styles.navLabel}>Nowe udostępnienie</span>
            <span className={styles.navLabelShort}>Nowe</span>
          </button>
          <button className={`${styles.navBtn} ${tab === 'users' ? styles.active : ''}`} onClick={() => { setTab('users'); fetchUsers(); }} title="Użytkownicy">
            <Users size={20} strokeWidth={1.75} />
            <span className={styles.navLabel}>Użytkownicy</span>
            <span className={styles.navLabelShort}>Osoby</span>
          </button>
          <button className={`${styles.navBtn} ${tab === 'inbox' ? styles.active : ''}`} onClick={() => { setTab('inbox'); fetchInbox(); }} title={`Odebrane (${inbox.length})`}>
            <Inbox size={20} strokeWidth={1.75} />
            <span className={styles.navLabel}>Odebrane</span>
            <span className={styles.navLabelShort}>Odebrane</span>
            <span className={styles.navCount}>{inbox.length}</span>
          </button>
          <button className={`${styles.navBtn} ${tab === 'audit' ? styles.active : ''}`} onClick={() => { setTab('audit'); fetchAuditLog(); }} title="Logi systemu">
            <ScrollText size={20} strokeWidth={1.75} />
            <span className={styles.navLabel}>Logi systemu</span>
            <span className={styles.navLabelShort}>Logi</span>
          </button>
          <button className={`${styles.navBtn} ${styles.navBtnMobileOnly}`} onClick={logout} title="Wyloguj">
            <LogOut size={20} strokeWidth={1.75} />
            <span className={styles.navLabelShort}>Wyjdź</span>
          </button>
        </nav>

        <div className={styles.sidebarFooter}>
          <button onClick={logout} title="Wyloguj" style={{ fontSize: '12px', ...btnIcon, justifyContent: 'center' }}>
            <LogOut size={15} strokeWidth={1.75} />
            <span className={styles.btnLabelSidebar}>Wyloguj</span>
          </button>
        </div>
      </aside>

      <main className={styles.main}>

        {/* ─── Dashboard ─── */}
        {tab === 'dashboard' && (
          <>
            <div className={styles.pageHeader}>
              <h2>Dashboard</h2>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  className="danger"
                  onClick={deleteExpiredShares}
                  disabled={expiredCount === 0}
                  style={{ fontSize: '13px', ...btnIcon, opacity: expiredCount === 0 ? 0.5 : 1, cursor: expiredCount === 0 ? 'not-allowed' : 'pointer' }}
                  title={expiredCount === 0 ? 'Brak wygasłych linków' : `Usuń ${expiredCount} wygasłych linków`}
                >
                  <Trash2 size={14} strokeWidth={1.75} /> Usuń wygasłe{expiredCount > 0 ? ` (${expiredCount})` : ''}
                </button>
                <button onClick={() => { fetchShares(); fetchUsers(); }} style={{ fontSize: '13px', ...btnIcon }}><RefreshCw size={14} strokeWidth={1.75} /> Odśwież</button>
              </div>
            </div>

            <div className={styles.filterBar}>
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Szukaj — nazwa, plik, osoba, folder, status, IP..." autoComplete="off" />
              <select value={selectedFolder} onChange={e => setSelectedFolder(e.target.value)}>
                <option value="all">Wszystkie foldery ({shares.length})</option>
                <option value="none">Bez folderu ({noFolderCount})</option>
                {folders.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.name} ({shares.filter(s => s.folderId === f.id).length})
                  </option>
                ))}
              </select>
              <button
                className={styles.folderSettingsBtn}
                onClick={() => setFoldersModalOpen(true)}
                title="Zarządzaj folderami"
              >
                <Settings size={18} strokeWidth={1.75} />
              </button>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="newest">Najnowsze</option>
                <option value="oldest">Najstarsze</option>
                <option value="name">Nazwa A-Z</option>
                <option value="downloads">Popularne</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <div className="section-label" style={{ marginBottom: 0 }}>Wszystkie udostępnienia</div>
              {selectedShares.size > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Zaznaczono: {selectedShares.size}</span>
                  <button onClick={() => setSelectedShares(new Set())} style={{ fontSize: '12px', padding: '5px 10px' }}>Odznacz</button>
                  <button className="danger" onClick={bulkDeleteShares} style={{ fontSize: '12px', padding: '5px 10px', ...btnIcon }}><Trash2 size={12} strokeWidth={1.75} /> Usuń zaznaczone</button>
                </div>
              )}
            </div>

            {loading && <div style={{ color: 'var(--text-muted)', padding: '1rem' }}>Ładowanie...</div>}
            {!loading && shares.length === 0 && (
              <div className="empty-state"><div className="icon"><Inbox size={40} strokeWidth={1.5} /></div><h3>Brak udostępnień</h3><p>Utwórz pierwsze udostępnienie</p></div>
            )}
            {!loading && shares.length > 0 && visibleShares.length === 0 && (
              <div className="empty-state"><div className="icon"><Inbox size={40} strokeWidth={1.5} /></div><h3>Brak wyników</h3><p>Nic nie pasuje do zapytania</p></div>
            )}

            {visibleShares.map(s => (
              <ShareCard
                key={s.id}
                s={s}
                selected={selectedShares.has(s.id)}
                onToggleSelect={toggleSelectShare}
                folders={folders}
                users={users}
                onMoveToFolder={moveShareToFolder}
                onCopy={copyToClipboard}
                onEdit={openEdit}
                onShowLogs={setLogShare}
                onToggleActive={toggleShare}
                onDelete={deleteShare}
              />
            ))}
          </>
        )}

        {/* ─── Nowe udostępnienie ─── */}
        {tab === 'new' && (
          <>
            <div className={styles.pageHeader}><h2>Nowe udostępnienie</h2></div>

            {createdLink && (
              <div className="card" style={{ borderColor: 'var(--green-border)', marginBottom: '1.5rem' }}>
                <div style={{ fontWeight: 500, color: 'var(--green-text)', marginBottom: '8px', ...btnIcon }}><CheckCircle size={16} strokeWidth={1.75} /> Link gotowy do wysłania</div>
                <div className={styles.linkRow}>
                  <span className={styles.linkText}>{createdLink.url}</span>
                  <button onClick={() => copyToClipboard(createdLink.url)} style={{ padding: '4px 10px', fontSize: '12px' }}>Kopiuj</button>
                  <a href={createdLink.url} target="_blank" rel="noreferrer"><button style={{ padding: '4px 10px', fontSize: '12px', ...btnIcon }}>Otwórz <ArrowUpRight size={12} strokeWidth={2} /></button></a>
                </div>
              </div>
            )}

            <div className="card">
              <div className="section-label">Pliki</div>
              <div
                className={`${styles.dropZone} ${dragging ? styles.dragging : ''}`}
                onClick={() => document.getElementById('fileInput').click()}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
              >
                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Upload size={32} strokeWidth={1.5} /></div>
                <div style={{ fontWeight: 500, marginBottom: '4px' }}>Przeciągnij pliki lub kliknij</div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Dowolne formaty, do 2 GB</div>
              </div>
              <input type="file" id="fileInput" multiple style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
              {files.length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  {files.map((f, i) => (
                    <div key={i} className={styles.fileItem}>
                      <FileIcon name={f.name} boxSize={32} size={16} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{formatSize(f.size)}</div>
                      </div>
                      <button className="danger" style={{ padding: '3px 8px', fontSize: '12px' }} onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}><X size={12} strokeWidth={2} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card">
              <div className="section-label">Ustawienia linku</div>
              <div className="field">
                <label>Nazwa dla klienta</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="np. Dokumenty projektowe Q2 2024" />
              </div>
              <div className="field">
                <label>Hasło (opcjonalne)</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Zostaw puste jeśli bez hasła" style={{ paddingRight: '40px' }} />
                  <span onClick={() => setShowPass(p => !p)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    {showPass ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                  </span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="field">
                <div>
                  <label>Wygasa po</label>
                  <select value={expiryDays} onChange={e => setExpiryDays(e.target.value)}>
                    <option value="1">1 dzień</option>
                    <option value="3">3 dni</option>
                    <option value="7">7 dni</option>
                    <option value="14">14 dni</option>
                    <option value="30">30 dni</option>
                    <option value="0">Nigdy</option>
                  </select>
                </div>
                <div>
                  <label>Limit pobrań</label>
                  <select value={downloadLimit} onChange={e => setDownloadLimit(e.target.value)}>
                    <option value="0">Bez limitu</option>
                    <option value="1">1 raz</option>
                    <option value="5">5 razy</option>
                    <option value="10">10 razy</option>
                    <option value="25">25 razy</option>
                    <option value="50">50 razy</option>
                  </select>
                </div>
              </div>
              <div className="field">
                <label>Folder</label>
                <select value={newShareFolderId} onChange={e => setNewShareFolderId(e.target.value)}>
                  <option value="">Bez folderu</option>
                  {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Wiadomość dla klienta</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows="3" placeholder="Opcjonalna wiadomość..." />
              </div>
              {uploading && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>Przesyłanie... {uploadProgress}%</div>
                  <div className="progress-bar-wrap"><div className="progress-bar-inner" style={{ width: uploadProgress + '%' }} /></div>
                </div>
              )}
              <button
                className={styles.createShareBtn}
                onClick={createShare}
                disabled={uploading}
                style={{ width: '100%', padding: '10px', fontSize: '14px', fontWeight: 500, borderRadius: '8px', cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}
              >
                {uploading ? 'Przesyłanie...' : 'Utwórz link do udostępnienia'}
              </button>
            </div>
          </>
        )}

        {/* ─── Użytkownicy ─── */}
        {tab === 'users' && (
          <>
            <div className={styles.pageHeader}><h2>Użytkownicy</h2></div>

            <div className="card">
              <div className="section-label">Dodaj nowego użytkownika</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }} className="field">
                <div><label>Imię i nazwisko</label><input value={newUserName} onChange={e => setNewUserName(e.target.value)} placeholder="Jan Kowalski" /></div>
                <div><label>Email</label><input type="email" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} placeholder="jan@firma.pl" /></div>
                <div><label>Hasło</label><input type="password" value={newUserPass} onChange={e => setNewUserPass(e.target.value)} placeholder="••••••••" /></div>
              </div>
              {userError && <div style={{ color: 'var(--red-text)', fontSize: '13px', marginBottom: '0.75rem', ...btnIcon }}><AlertCircle size={14} strokeWidth={1.75} /> {userError}</div>}
              <button className="primary" onClick={createUser}>Utwórz konto</button>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' }}>
                Użytkownik loguje się pod adresem: <strong>{window.location.origin}/panel</strong>
              </div>
            </div>

            <div className="section-label">Lista użytkowników</div>
            {users.length === 0 && (
              <div className="empty-state"><div className="icon"><Users size={40} strokeWidth={1.5} /></div><h3>Brak użytkowników</h3><p>Dodaj pierwszego użytkownika powyżej</p></div>
            )}
            {users.map(u => (
              <div className="card" key={u.id}>
                <div className={styles.shareRow}>
                  <div className={styles.shareInfo}>
                    <div className={styles.shareName}>
                      {u.name}
                      <span className={`badge ${u.active ? 'green' : 'amber'}`}>{u.active ? 'Aktywny' : 'Wyłączony'}</span>
                      {u.inboxAccess && <span className="badge blue"><Inbox size={11} strokeWidth={2} /> Odebrane</span>}
                    </div>
                    <div className={styles.shareMeta}>{u.email} · Utworzony {formatDate(u.createdAt)} · Udostępnień: {u.sharesCount}</div>
                  </div>
                  <div className={styles.shareActions}>
                    <button onClick={() => { setResetPassUser(u); setResetPassValue(''); setResetPassError(''); }} style={{ fontSize: '12px', padding: '5px 10px', ...btnIcon }}><KeyRound size={12} strokeWidth={1.75} /> Hasło</button>
                    <button onClick={() => toggleInbox(u.id)} style={{ fontSize: '12px', padding: '5px 10px', ...btnIcon }}><Inbox size={12} strokeWidth={1.75} /> {u.inboxAccess ? 'Wyłącz Odebrane' : 'Włącz Odebrane'}</button>
                    <button onClick={() => toggleUser(u.id)} style={{ fontSize: '12px', padding: '5px 10px' }}>{u.active ? 'Wyłącz' : 'Włącz'}</button>
                    <button className="danger" onClick={() => deleteUser(u.id)} style={{ fontSize: '12px', padding: '5px 10px' }}>Usuń</button>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ─── Odebrane ─── */}
        {tab === 'inbox' && (
          <>
            <div className={styles.pageHeader}>
              <h2 style={{ ...btnIcon, gap: '8px' }}><Inbox size={22} strokeWidth={1.75} /> Odebrane od klientów</h2>
              <button onClick={fetchInbox} style={{ fontSize: '13px', ...btnIcon }}><RefreshCw size={14} strokeWidth={1.75} /> Odśwież</button>
            </div>
            {inbox.length === 0 && (
              <div className="empty-state">
                <div className="icon"><MailOpen size={40} strokeWidth={1.5} /></div>
                <h3>Brak przesyłek</h3>
                <p>Klienci mogą wysyłać pliki na <strong>{window.location.origin}</strong></p>
              </div>
            )}
            {inbox.map(u => (
              <div className={styles.inboxRow} key={u.id} onClick={() => openInboxItem(u.id)}>
                <span className={styles.inboxDate}><Calendar size={13} strokeWidth={1.75} /> {formatDate(u.createdAt)}</span>
                <span className={styles.inboxSender}><User size={13} strokeWidth={1.75} style={{ color: '#86efac' }} /> {u.senderName || u.senderEmail}</span>
                <span className={styles.inboxEmail}>
                  {u.senderName && <><Mail size={13} strokeWidth={1.75} style={{ color: 'rgba(255,255,255,0.35)' }} /> {u.senderEmail}</>}
                </span>
                <span className={styles.inboxCount}><FileText size={13} strokeWidth={1.75} /> {u.fileCount} {u.fileCount === 1 ? 'plik' : 'pliki'}</span>
                <span className={styles.inboxSize}><HardDrive size={13} strokeWidth={1.75} /> {formatSize(u.totalSize)}</span>
              </div>
            ))}
          </>
        )}

        {/* ─── Logi systemu ─── */}
        {tab === 'audit' && (
          <>
            <div className={styles.pageHeader}>
              <h2 style={{ ...btnIcon, gap: '8px' }}><ScrollText size={22} strokeWidth={1.75} /> Logi systemu</h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={fetchAuditLog} style={{ fontSize: '13px', ...btnIcon }}><RefreshCw size={14} strokeWidth={1.75} /> Odśwież</button>
                <button onClick={clearAuditLog} className="danger" style={{ fontSize: '13px', ...btnIcon }}><Trash2 size={14} strokeWidth={1.75} /> Wyczyść</button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '6px', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
              <button onClick={() => setAuditFilter('all')} className={auditFilter === 'all' ? 'primary' : ''} style={{ fontSize: '12px', padding: '5px 12px' }}>Wszystkie ({auditLog.length})</button>
              <button onClick={() => setAuditFilter('login')} className={auditFilter === 'login' ? 'primary' : ''} style={{ fontSize: '12px', padding: '5px 12px', ...btnIcon }}><Lock size={12} strokeWidth={1.75} /> Logowania</button>
              <button onClick={() => setAuditFilter('share')} className={auditFilter === 'share' ? 'primary' : ''} style={{ fontSize: '12px', padding: '5px 12px', ...btnIcon }}><Upload size={12} strokeWidth={1.75} /> Udostępnienia</button>
              <button onClick={() => setAuditFilter('user')} className={auditFilter === 'user' ? 'primary' : ''} style={{ fontSize: '12px', padding: '5px 12px', ...btnIcon }}><Users size={12} strokeWidth={1.75} /> Użytkownicy</button>
              <button onClick={() => setAuditFilter('inbox')} className={auditFilter === 'inbox' ? 'primary' : ''} style={{ fontSize: '12px', padding: '5px 12px', ...btnIcon }}><Inbox size={12} strokeWidth={1.75} /> Skrzynka</button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <input
                value={auditSearchQuery}
                onChange={e => setAuditSearchQuery(e.target.value)}
                placeholder="Szukaj w logach — osoba, akcja, szczegóły, IP, data..."
                autoComplete="off"
                style={{ width: '100%' }}
              />
            </div>

            {auditLog.length === 0 && (
              <div className="empty-state">
                <div className="icon"><ScrollText size={40} strokeWidth={1.5} /></div>
                <h3>Brak logów</h3>
                <p>Tu pojawią się wszystkie zdarzenia systemowe</p>
              </div>
            )}

            {(() => {
              const filtered = auditLog
                .filter(e => {
                  if (auditFilter === 'all') return true;
                  if (auditFilter === 'login') return e.type.startsWith('login') || e.type.startsWith('password');
                  if (auditFilter === 'share') return e.type.startsWith('share');
                  if (auditFilter === 'user') return e.type.startsWith('user');
                  if (auditFilter === 'inbox') return e.type.startsWith('inbox');
                  return true;
                })
                .filter(e => {
                  if (!auditSearchQuery.trim()) return true;
                  const q = auditSearchQuery.toLowerCase();
                  const haystack = [AUDIT_LABELS[e.type] || e.type, e.actorName, e.actor, e.details, e.ip, formatDateTime(e.at)]
                    .filter(Boolean).join(' ').toLowerCase();
                  return haystack.includes(q);
                });

              if (auditLog.length > 0 && filtered.length === 0) {
                return (
                  <div className="empty-state">
                    <div className="icon"><ScrollText size={40} strokeWidth={1.5} /></div>
                    <h3>Brak wyników</h3>
                    <p>Nic nie pasuje do zapytania</p>
                  </div>
                );
              }

              let lastDay = null;
              return filtered.map((e, i) => {
                const day = formatDate(e.at);
                const showDayHeader = day !== lastDay;
                lastDay = day;
                const time = new Date(e.at).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
                const color = AUDIT_COLORS[e.type] || '#8b8b9e';
                return (
                  <div key={i}>
                    {showDayHeader && (
                      <div style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase', padding: i === 0 ? '0 2px 6px' : '14px 2px 6px' }}>{day}</div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '6px 10px', marginBottom: '2px', borderRadius: '6px', background: 'rgba(255,255,255,0.02)', overflow: 'hidden' }}>
                      <span style={{ color, fontWeight: 500, flexShrink: 0 }}>{AUDIT_LABELS[e.type] || e.type}</span>
                      <span style={{ color: 'var(--text-muted)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.actorName || e.actor || '?'}{e.details && ` · ${e.details}`}
                      </span>
                      {e.ip && <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{e.ip}</span>}
                      <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{time}</span>
                    </div>
                  </div>
                );
              });
            })()}
          </>
        )}
      </main>

      {toast && <div className={styles.toast}>{toast}</div>}

      {/* ─── Modal: zarządzanie folderami ─── */}
      {foldersModalOpen && (
        <div onClick={() => setFoldersModalOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700 }}>Zarządzaj folderami</h2>
              <button onClick={() => setFoldersModalOpen(false)} style={{ padding: '4px 10px' }}><X size={14} strokeWidth={2} /></button>
            </div>

            <div style={{ display: 'flex', gap: '6px', marginBottom: '1.25rem' }}>
              <input
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createFolder()}
                placeholder="Nazwa nowego folderu..."
                style={{ flex: 1, minWidth: 0 }}
                autoComplete="off"
              />
              <button onClick={createFolder} style={{ flex: '0 0 auto', ...btnIcon }}><Plus size={15} strokeWidth={1.75} /> Dodaj</button>
            </div>

            <div className="section-label">Twoje foldery</div>
            {folders.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '1rem 0', textAlign: 'center' }}>
                Brak folderów — dodaj pierwszy powyżej
              </div>
            )}
            {folders.map(f => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 11px', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', marginBottom: '4px' }}>
                <Folder size={15} strokeWidth={1.75} style={{ color: '#c4b5fd', flexShrink: 0 }} />
                <span style={{ fontSize: '13px', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                <span style={{ fontSize: '10.5px', color: 'var(--text-muted)', flexShrink: 0 }}>
                  {shares.filter(s => s.folderId === f.id).length} linków
                </span>
                <button className="danger" onClick={() => deleteFolder(f.id)} title="Usuń folder" style={{ padding: '4px 7px', flexShrink: 0 }}>
                  <Trash2 size={14} strokeWidth={1.75} />
                </button>
              </div>
            ))}

            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '14px', lineHeight: 1.5 }}>
              Usunięcie folderu nie kasuje linków — zostaną przeniesione do „Bez folderu".
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal: reset hasła użytkownika ─── */}
      {resetPassUser && (
        <div onClick={() => setResetPassUser(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '400px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700 }}>Zmień hasło</h2>
              <button onClick={() => setResetPassUser(null)} style={{ padding: '4px 10px' }}><X size={14} strokeWidth={2} /></button>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '1rem' }}>Ustaw nowe hasło dla <strong>{resetPassUser.name}</strong> ({resetPassUser.email})</p>
            <div className="field">
              <label>Nowe hasło</label>
              <input type="text" value={resetPassValue} onChange={e => setResetPassValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && resetUserPassword()} placeholder="Minimum 6 znaków" autoFocus />
            </div>
            {resetPassError && <div style={{ color: 'var(--red-text)', fontSize: '13px', marginBottom: '1rem', ...btnIcon }}><AlertCircle size={14} strokeWidth={1.75} /> {resetPassError}</div>}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setResetPassUser(null)} style={{ flex: 1 }}>Anuluj</button>
              <button className="primary" onClick={resetUserPassword} style={{ flex: 1 }}>Zmień hasło</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal: edycja udostępnienia ─── */}
      {editingShare && (
        <div onClick={() => setEditingShare(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700 }}>Edytuj udostępnienie</h2>
              <button onClick={() => setEditingShare(null)} style={{ padding: '4px 10px' }}><X size={14} strokeWidth={2} /></button>
            </div>
            <div className="field">
              <label>Nazwa</label>
              <input value={editName} onChange={e => setEditName(e.target.value)} />
            </div>
            <div className="field">
              <label>Wiadomość dla klienta</label>
              <textarea value={editMessage} onChange={e => setEditMessage(e.target.value)} rows="3" />
            </div>
            <div className="field">
              <label>{editingShare.hasPassword ? 'Zmień hasło (zostaw puste aby nie zmieniać)' : 'Dodaj hasło (opcjonalne)'}</label>
              <input type="text" value={editPassword} onChange={e => { setEditPassword(e.target.value); setEditRemovePass(false); }} placeholder={editingShare.hasPassword ? 'Nowe hasło...' : 'Zostaw puste jeśli bez hasła'} disabled={editRemovePass} />
              {editingShare.hasPassword && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', textTransform: 'none', fontWeight: 400, fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editRemovePass} onChange={e => { setEditRemovePass(e.target.checked); if (e.target.checked) setEditPassword(''); }} style={{ width: 'auto' }} />
                  Usuń hasło (link będzie dostępny bez logowania)
                </label>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="field">
              <div>
                <label>Wygasa za (dni od teraz)</label>
                <select value={editExpiryDays} onChange={e => setEditExpiryDays(e.target.value)}>
                  <option value="1">1 dzień</option>
                  <option value="3">3 dni</option>
                  <option value="7">7 dni</option>
                  <option value="14">14 dni</option>
                  <option value="30">30 dni</option>
                  <option value="0">Nigdy</option>
                </select>
              </div>
              <div>
                <label>Limit pobrań</label>
                <select value={editDownloadLimit} onChange={e => setEditDownloadLimit(e.target.value)}>
                  <option value="0">Bez limitu</option>
                  <option value="1">1 raz</option>
                  <option value="5">5 razy</option>
                  <option value="10">10 razy</option>
                  <option value="25">25 razy</option>
                  <option value="50">50 razy</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
              <button onClick={() => setEditingShare(null)} style={{ flex: 1 }}>Anuluj</button>
              <button className="primary" onClick={saveEdit} style={{ flex: 1 }}>Zapisz zmiany</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal: przesyłka od klienta ─── */}
      {openUpload && (
        <div onClick={() => setOpenUpload(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700 }}>Przesyłka od klienta</h2>
              <button onClick={() => setOpenUpload(null)} style={{ padding: '4px 10px' }}><X size={14} strokeWidth={2} /></button>
            </div>
            <div style={{ marginBottom: '1rem', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ fontSize: '13px', marginBottom: '4px' }}><strong>Od:</strong> {openUpload.senderName || '—'}</div>
              <div style={{ fontSize: '13px', marginBottom: '4px' }}><strong>Email:</strong> {openUpload.senderEmail}</div>
              <div style={{ fontSize: '13px', marginBottom: '4px' }}><strong>Wysłano:</strong> {new Date(openUpload.createdAt).toLocaleString('pl-PL')}</div>
              {openUpload.message && <div style={{ fontSize: '13px', marginTop: '8px', fontStyle: 'italic', padding: '10px 12px', color: 'rgba(255,255,255,0.92)', borderRadius: '8px', boxShadow: 'inset 0 0 0 999px rgba(124,58,237,0.14)' }}>„{openUpload.message}"</div>}
            </div>
            <div className="section-label">Pliki ({openUpload.files.length})</div>
            {openUpload.files.map((f, i) => (
              <div key={i} className={styles.fileItem}>
                <FileIcon name={f.name} boxSize={32} size={16} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{formatSize(f.size)}</div>
                </div>
                <button onClick={() => downloadInboxFile(openUpload.id, f.name)} style={{ fontSize: '12px', padding: '5px 10px', ...btnIcon }}><Download size={12} strokeWidth={1.75} /> Pobierz</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
              <button onClick={() => downloadInboxZip(openUpload.id)} className="primary" style={{ flex: 1, ...btnIcon, justifyContent: 'center' }}><Package size={14} strokeWidth={1.75} /> Pobierz wszystko (ZIP)</button>
              <button onClick={() => deleteInboxItem(openUpload.id)} className="danger" style={{ flex: '0 0 auto', ...btnIcon }}><Trash2 size={14} strokeWidth={1.75} /> Usuń</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal: logi pobrań ─── */}
      {logShare && (
        <div onClick={() => setLogShare(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, ...btnIcon, gap: '8px' }}><FileText size={18} strokeWidth={1.75} /> Logi pobrań</h2>
              <button onClick={() => setLogShare(null)} style={{ padding: '4px 10px' }}><X size={14} strokeWidth={2} /></button>
            </div>
            <div style={{ marginBottom: '1rem', fontSize: '13px', color: 'var(--text-muted)' }}>
              <strong>{logShare.name}</strong> · Łącznie: {logShare.downloads} {logShare.downloads === 1 ? 'pobranie' : 'pobrań'}
            </div>
            {(!logShare.downloadLog || logShare.downloadLog.length === 0) && (
              <div className="empty-state" style={{ padding: '2rem 1rem' }}>
                <div className="icon"><FileText size={40} strokeWidth={1.5} /></div>
                <h3>Brak logów</h3>
                <p>Ten link jeszcze nie został pobrany</p>
              </div>
            )}
            {logShare.downloadLog && logShare.downloadLog.length > 0 && (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {[...logShare.downloadLog].reverse().map((log, i) => (
                  <div key={i} style={{ padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: '8px', marginBottom: '6px', fontSize: '12px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '3px', ...btnIcon }}>
                      {log.filename === '__ALL__ZIP__'
                        ? <><Package size={13} strokeWidth={1.75} /> Pobrano wszystko (ZIP)</>
                        : <><FileText size={13} strokeWidth={1.75} /> {log.filename}</>}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', ...btnIcon }}>
                      <Clock size={11} strokeWidth={1.75} /> {formatDateTime(log.at)}
                      <Globe size={11} strokeWidth={1.75} style={{ marginLeft: '4px' }} /> {log.ip || '—'}
                    </div>
                    {log.userAgent && (
                      <div style={{ color: 'var(--text-muted)', fontSize: '10px', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.userAgent}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}